import { useRef, useState } from 'react';
import { Paperclip, Upload, Link2, Download, Trash2, FileText } from 'lucide-react';
import { useAttachments, Attachment } from '../../hooks/useAttachments';

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function recipientSummary(a: Attachment): string {
  if (!a.recipients?.length) return '';
  return a.recipients.map(r => `${r.user?.name ?? 'Unknown'}${r.downloadedAt ? ' ✓' : ' (pending)'}`).join(', ');
}

interface Props {
  ticketId: string;
  currentUserId: string | undefined;
  /** Admin/IT Manager — the only ones who may delete either kind of attachment. */
  isAdminOrManager: boolean;
}

export default function AttachmentsPanel({ ticketId, currentUserId, isAdminOrManager }: Props) {
  const {
    attachments, participants, loading, uploading,
    upload, addLink, download, remove, loadParticipants,
  } = useAttachments(ticketId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [showLinkForm, setShowLinkForm]     = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [linkUrl, setLinkUrl]               = useState('');
  const [recipientIds, setRecipientIds]     = useState<string[]>([]);

  const openUploadForm = () => {
    setShowUploadForm(v => !v);
    if (!showUploadForm) loadParticipants();
  };

  const toggleRecipient = (id: string) =>
    setRecipientIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) upload(f, recipientIds).then(() => { setRecipientIds([]); setShowUploadForm(false); });
    e.target.value = '';
  };

  const submitLink = async () => {
    if (!linkUrl.trim()) return;
    await addLink(linkUrl.trim());
    setLinkUrl(''); setShowLinkForm(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-navy">
          <Paperclip size={14} /> Attachments {attachments.length > 0 && <span className="text-gray-400">({attachments.length})</span>}
        </h4>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowLinkForm(v => !v)} disabled={uploading}
            title="Attach a SharePoint/OneDrive link — opened and edited centrally, never downloaded"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline disabled:opacity-50">
            <Link2 size={13} /> Attach Shared Link
          </button>
          <button onClick={openUploadForm} disabled={uploading}
            title="Send a one-time file to one or more people on this ticket — removed for each once they download it"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline disabled:opacity-50">
            <Upload size={13} /> {uploading ? 'Sending…' : 'Upload File'}
          </button>
          <input ref={fileRef} type="file" hidden onChange={onFile} />
        </div>
      </div>

      {showUploadForm && (
        <div className="mb-3 p-2.5 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
          <p className="text-xs font-medium text-gray-600">Send this file to:</p>
          {participants.length === 0 ? (
            <p className="text-[11px] text-gray-400">No one else is on this ticket yet to send a file to.</p>
          ) : (
            <div className="space-y-1">
              {participants.map(p => (
                <label key={p.id} className="flex items-center gap-2 text-xs text-navy">
                  <input type="checkbox" checked={recipientIds.includes(p.id)} onChange={() => toggleRecipient(p.id)} />
                  {p.name}
                </label>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400">Each person gets their own one-time download — the file is removed for them once they download it, and removed entirely once everyone selected has.</p>
          <div className="flex gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading || !recipientIds.length}
              className="text-xs font-medium text-white bg-brand rounded-md px-3 py-1.5 disabled:opacity-50">
              {uploading ? 'Sending…' : 'Choose File & Send'}
            </button>
            <button onClick={() => { setShowUploadForm(false); setRecipientIds([]); }}
              className="text-xs font-medium text-gray-500 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {showLinkForm && (
        <div className="mb-3 p-2.5 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
          <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="Paste the SharePoint / OneDrive share link (https://…)"
            className="w-full text-xs rounded-md border border-gray-200 px-2.5 py-1.5 outline-none focus:border-brand" />
          <p className="text-[11px] text-gray-400">Opens in the browser/desktop app to view, comment or sign — per whatever access that link grants. Nothing is downloaded or deleted here; everyone works off the same central file.</p>
          <div className="flex gap-2">
            <button onClick={submitLink} disabled={uploading || !linkUrl.trim()}
              className="text-xs font-medium text-white bg-brand rounded-md px-3 py-1.5 disabled:opacity-50">
              {uploading ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => { setShowLinkForm(false); setLinkUrl(''); }}
              className="text-xs font-medium text-gray-500 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-gray-400">No proof documents attached yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map(a => {
            const isLink = a.resourceType === 'link';
            const myRecipient = a.recipients?.find(r => r.userId === currentUserId);
            const canDownload = !isLink && !!myRecipient && !myRecipient.downloadedAt;
            return (
              <li key={a.id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:bg-gray-50 group">
                {isLink
                  ? <Link2 size={16} className="text-gray-400 shrink-0" />
                  : <FileText size={16} className="text-gray-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  {isLink ? (
                    <button onClick={() => download(a)}
                      className="text-sm text-blue-600 underline hover:text-blue-800 truncate block text-left">
                      {a.fileName}
                    </button>
                  ) : (
                    <div className="text-sm text-navy truncate">{a.fileName}</div>
                  )}
                  <div className="text-xs text-gray-400 truncate">
                    {isLink ? 'Shared document' : `${fmtSize(a.fileSize ?? 0)} · ${recipientSummary(a)}`} · from {a.uploadedBy?.name ?? 'Unknown'}
                  </div>
                </div>
                {canDownload && (
                  <button onClick={() => download(a)} className="text-gray-400 hover:text-brand p-1"
                    title="Download (this removes your copy — and the file entirely once everyone it was sent to has downloaded theirs)" aria-label="Download">
                    <Download size={15} />
                  </button>
                )}
                {isAdminOrManager && (
                  <button onClick={() => remove(a)} className="text-gray-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100" aria-label="Delete">
                    <Trash2 size={15} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
